import { useCallback, useEffect, useRef, useState } from 'react';

export default function useAutoScroll({
    messages,
    activeMessage,
    hasMore,
    isLoading,
    loadMore,
    responseAnchorRef = null,
    preferResponseStart = false,
}) {
    const messageEndRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

    // Keep the latest flags in refs so the auto-scroll effect can read them WITHOUT
    // listing them as dependencies. Otherwise a transient re-render — e.g. the browser
    // blurring/refocusing the input and resizing the viewport when the user takes a
    // native screenshot — flips `preferResponseStart`/`shouldAutoScroll` and re-fires
    // the effect, snapping the view to the top of the latest response even though no new
    // message arrived. The effect must only run on genuine content changes.
    const shouldAutoScrollRef = useRef(shouldAutoScroll);
    shouldAutoScrollRef.current = shouldAutoScroll;
    const preferResponseStartRef = useRef(preferResponseStart);
    preferResponseStartRef.current = preferResponseStart;

    useEffect(() => {
        if (!shouldAutoScrollRef.current) return;
        if (preferResponseStartRef.current && responseAnchorRef?.current) {
            responseAnchorRef.current.scrollIntoView({
                behavior: 'auto',
                block: 'start',
            });
            return;
        }

        const container = scrollContainerRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
            return;
        }
        messageEndRef.current?.scrollIntoView({ behavior: 'auto' });
        // Only react to real content changes (new/streaming messages), never to
        // transient focus/resize-driven re-renders such as a screenshot.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, activeMessage]);

    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;

        const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShouldAutoScroll(isAtBottom);

        if (scrollTop < 50 && hasMore && !isLoading && messages.length > 0) {
            loadMore(messages.length);
        }
    }, [hasMore, isLoading, loadMore, messages.length]);

    const scrollToBottom = useCallback((behavior = 'smooth') => {
        const container = scrollContainerRef.current;
        if (container) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior,
            });
            setShouldAutoScroll(true);
            return;
        }
        messageEndRef.current?.scrollIntoView({ behavior });
        setShouldAutoScroll(true);
    }, []);

    return {
        messageEndRef,
        scrollContainerRef,
        shouldAutoScroll,
        setShouldAutoScroll,
        handleScroll,
        scrollToBottom,
    };
}
